import type { SignerReport, VerificationReport } from './api'

/**
 * Which one sentence the verify page leads with for a report.
 *
 * It is a module of its own so the order can be tested, because the order is
 * the claim. "Intact" is the only green answer, and it is reached only when
 * every signature matches the bytes it signed AND a seal the reader can rely
 * on reaches the end of the file. Content added after the last seal is
 * covered by none of them, so every one still matches, and a page that asked
 * only the first question would show green over a file whose content changed
 * after signing. When a trusted seal is followed by content only untrusted
 * signatures cover, the page says it was the trusted seal the content came
 * after, because a signature anybody can make covers it.
 *
 * When no seal is trusted, the page leads with whether docsdone recognises the
 * exact file as one it sealed (known_to_us), and says that is what it rests
 * on: every name on an untrusted signature is whatever its holder wrote,
 * docsdone's included. "Intact" is kept for a trusted seal over the file.
 *
 * A file carrying only document timestamps is not a signed document: a
 * timestamp shows the bytes existed at a time, and nobody signed anything.
 * A file with more signatures than docsdone checks had none checked, and is
 * told apart by the API's code, never by its error's wording.
 */
export type Verdict =
  | 'not_checked'
  | 'unsigned'
  | 'timestamp_only'
  | 'not_intact'
  | 'added_after_trusted'
  | 'added_after'
  | 'not_recognised'
  | 'recognised'
  | 'intact'

export function verdict(report: VerificationReport): Verdict {
  if (report.code === 'too_many_signatures') return 'not_checked'
  const signers = report.signers ?? []
  if (!signers.length) return 'unsigned'
  if (signers.every((s) => s.document_timestamp)) return 'timestamp_only'
  if (!report.intact) return 'not_intact'
  const trustedSeal = signers.some((s) => s.valid_signature && s.trusted_issuer && !s.document_timestamp)
  if (!report.covers_whole_file) return trustedSeal ? 'added_after_trusted' : 'added_after'
  // Nobody this machine trusts signed it, so a name on a signature is only a
  // claim, and anybody can lay a signature carrying docsdone's name over a
  // file. What remains is docsdone's own record of what it sealed, and the
  // page says that is what it rests on, not leave it to look like a trusted
  // certificate's green.
  if (!trustedSeal) return report.known_to_us ? 'recognised' : 'not_recognised'
  return 'intact'
}

// Why bytes after the last signature are not simply "content": a signing
// service adds the data that keeps a signature checkable for years — the
// issuer's statements, then a document timestamp — exactly this way, after
// the signature. A change to the document looks the same, and nothing in the
// file tells the two apart, so the page says both and does not pick.
const cannotTell =
  'A signing service adds validation data this way, and a change to the document looks just the same; ' +
  'this page cannot tell which.'

/** What the page leads with when bytes follow the last signature. */
export function bytesAddedAfter(which: 'added_after' | 'added_after_trusted'): { heading: string; body: string } {
  return which === 'added_after_trusted'
    ? {
        heading: 'Bytes added after the trusted signature.',
        body:
          'Every signature still matches the bytes it signed, but bytes were added to this file after the last ' +
          `signature this machine trusts, and no signature it trusts covers them. ${cannotTell} What the file ` +
          'shows now may differ from what the trusted signer signed.',
      }
    : {
        heading: 'Bytes added after signing.',
        body:
          'Every signature still matches the bytes it signed, but bytes were added to this file after the last ' +
          `signature, and no signature covers them. ${cannotTell} What the file shows now may differ from what ` +
          'was signed.',
      }
}

/**
 * Which version of the file one signature covers. A signature that does not
 * reach the end of the file signed an earlier version; saying whether a later
 * signature covers what was added after it is what lets a reader see who
 * vouches for the additions. Null when the report does not know.
 */
export function versionCovered(s: SignerReport): string | null {
  switch (s.covers) {
    case 'whole_file':
      return 'This file as it is now'
    case 'earlier_version_signed_after':
      return 'An earlier version of this file. Later signatures cover what was added after it.'
    case 'earlier_version_signed_after_untrusted':
      return 'An earlier version of this file. Only signatures this machine does not trust cover what was added after it.'
    case 'earlier_version':
      return 'An earlier version of this file. No signature covers what was added after it.'
    default:
      return null
  }
}

/**
 * What the page says of one signature's certificate. A certificate the file
 * itself says was revoked is never called trusted, and the page says so
 * rather than leaving it to "not trusted", which would hide why.
 */
export function issuerTrust(s: SignerReport): string {
  if (s.revoked_certificate) return 'Revoked: the file itself says a certificate in this signature was revoked'
  return s.trusted_issuer ? 'Trusted' : 'Not trusted by this machine'
}
